/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/usePinListForm.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing PIN List form state and submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createPinList, updatePinList } from '../api/pinListsApi';
import { FormState, PinMember, ValidationErrors } from '../types';
import { validatePinListForm } from '../utils/validators';
import { getDefaultFormState, DEFAULT_MEMBER, createTempId } from '../utils/formDefaults';
import { toApiPayloadMember, normalizeMemberForCompare } from '../utils/memberHelpers';

/**
 * Hook for PIN List form management
 */
export function usePinListForm(
  initialFormData: FormState | null,
  initialMembers: PinMember[] | null,
  id: string | undefined,
  isEditMode: boolean
) {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const lastSaveRef = useRef<number>(0);

  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formData, setFormData] = useState<FormState>(
    initialFormData || defaultFormState
  );
  const [members, setMembers] = useState<PinMember[]>(
    initialMembers || []
  );
  const [originalData, setOriginalData] = useState<FormState>(
    initialFormData || defaultFormState
  );
  const [originalMembers, setOriginalMembers] = useState<PinMember[]>(
    initialMembers || []
  );
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  // Sync with initial data when it changes
  useEffect(() => {
    if (initialFormData) {
      const currentSerialized = JSON.stringify({ formData: initialFormData, members: initialMembers });
      const lastSerialized = JSON.stringify({ formData: originalData, members: originalMembers });

      if (currentSerialized !== lastSerialized) {
        setFormData(initialFormData);
        setMembers(initialMembers || []);
        setOriginalData(initialFormData);
        setOriginalMembers(initialMembers || []);
        setErrors({});
      }
    } else if (!isEditMode && !initialFormData) {
      // Only reset if not in edit mode and no initial data
      const defaultState = getDefaultFormState();
      setFormData(defaultState);
      setMembers([]);
      setOriginalData(defaultState);
      setOriginalMembers([]);
      setErrors({});
    }
  }, [initialFormData, initialMembers, isEditMode, originalData, originalMembers]);

  const hasChanges = useMemo(() => {
    const normalizedForm = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      enabled: formData.enabled
    };

    const normalizedOriginal = {
      name: originalData.name.trim(),
      description: originalData.description.trim(),
      enabled: originalData.enabled
    };

    if (JSON.stringify(normalizedForm) !== JSON.stringify(normalizedOriginal)) {
      return true;
    }

    const currentMembers = members.map(normalizeMemberForCompare);
    const baselineMembers = originalMembers.map(normalizeMemberForCompare);

    return JSON.stringify(currentMembers) !== JSON.stringify(baselineMembers);
  }, [formData, originalData, members, originalMembers]);

  const handleChange = useCallback(
    (field: keyof FormState, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => {
          const updated = { ...prev };
          delete updated[field];
          return updated;
        });
      }
    },
    [errors]
  );

  const addMember = useCallback(() => {
    setMembers((prev) => [
      ...prev,
      {
        ...DEFAULT_MEMBER,
        tempId: createTempId('temp'),
        pin: '',
        description: '',
        enabled: true
      }
    ]);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateMember = useCallback((index: number, field: keyof PinMember, value: any) => {
    setMembers((prev) =>
      prev.map((member, memberIndex) =>
        memberIndex === index ? { ...member, [field]: value } : member
      )
    );
    if (errors[`member_${index}_pin`]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[`member_${index}_pin`];
        return updated;
      });
    }
  }, [errors]);

  const removeMember = useCallback((index: number) => {
    setMembers((prev) => prev.filter((_, memberIndex) => memberIndex !== index));
  }, []);

  const validateForm = useCallback(() => {
    const validationErrors = validatePinListForm(formData, members, t);
    setErrors(validationErrors);
    return { isValid: Object.keys(validationErrors).length === 0, errors: validationErrors };
  }, [formData, members, t]);

  // Handle submit - Always-Active Save Standard: always attempt to save
  const handleSubmit = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
      showErrorRef.current(`${t('pinLists.validation.fixErrors')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }

    const now = Date.now();
    if (now - lastSaveRef.current < 1000) {
      return undefined;
    }
    lastSaveRef.current = now;

    setIsSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        enabled: formData.enabled,
        members: members.map(toApiPayloadMember)
      };

      const result = isEditMode
        ? await updatePinList(id!, payload)
        : await createPinList(payload);

      showSuccess(
        isEditMode
          ? t('pinLists.messages.updateSuccess')
          : t('pinLists.messages.createSuccess')
      );

      const nextOriginal = { ...formData };
      setOriginalData(nextOriginal);
      setFormData(nextOriginal);
      setOriginalMembers([...members]);
      setMembers([...members]);
      setErrors({});

      // Return saved data (navigation is now handled by useSaveWithAction)
      return result.data || { ...payload, id: result.data?.id };
    } catch (err) {
      showErrorRef.current(err instanceof Error ? err.message : t('pinLists.messages.createError'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [formData, members, hasChanges, id, isEditMode, showInfo, showSuccess, t, validateForm]);

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormData(originalData);
      setMembers(originalMembers);
      setErrors({});
    } else {
      const defaultState = getDefaultFormState();
      setFormData(defaultState);
      setMembers([]);
      setOriginalData(defaultState);
      setOriginalMembers([]);
      setErrors({});
    }
  }, [isEditMode, originalData, originalMembers]);

  return {
    formData,
    setFormData,
    members,
    setMembers,
    originalData,
    originalMembers,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    addMember,
    updateMember,
    removeMember,
    handleSubmit,
    handleCancel,
    validateForm
  };
}

